import React from 'react'
import { sessionContext, useGetBusiness } from '@marketing-milk/frontend'
import Badge from '@material-ui/core/Badge'
import Button from '@material-ui/core/Button'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import Fab from '@material-ui/core/Fab'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import CommentIcon from '@material-ui/icons/Comment'
import { CreativeStatus } from 'components/creative-status/CreativeStatus'
import { FormPageContent, FormPagePreview } from 'components/layout/PreviewForm'
import { CommentThread } from 'components/comments/CommentThread'
import { countUnresolvedRoots } from 'components/comments/commentCounts'
import { useComments } from 'hooks/api/useComments'
import { adCreativeFormatWire } from 'pages/business/creatives/creative-wizard/util'
import { useContext, useState } from 'react'
import { AdPreview } from './ad-preview/AdPreview'
import { BasicAdPreview } from './ad-preview/AdPreview.type'
import { creativeContext } from './context/CreativeContext'
import { CreativeContextProvider } from './context/CreativeContextProvider'
import './creative-wizard.scss'
import { CreativeWizardProps } from './CreativeWizard.types'
import { getCreativeFormat } from './format-views/get-format'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiFormLabel-root': {
        fontWeight: 'bold',
      },
    },
    paperContainer: {
      backgroundImage:
        "url('https://www.marketingmilk.com/wp-content/uploads/2021/07/empty-table-bg.jpg')",
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
    },
    commentsBadge: {
      marginLeft: theme.spacing(2),
      '& .MuiBadge-badge': {
        fontSize: '0.9rem',
        height: 20,
        minWidth: 20,
        padding: '0 6px',
      },
    },
  })
)

export const creativePreviews: Array<{ format: BasicAdPreview; icon: string }> = [
  { format: 'facebook', icon: 'fab fa-facebook-f' },
  { format: 'messenger', icon: 'fab fa-facebook-messenger' },
  { format: 'marketplace', icon: 'fas fa-store' },
  { format: 'instagram', icon: 'fab fa-instagram' },
]

export function CreativeWizardComponent() {
  const classes = useStyles()
  const business = useGetBusiness()
  const { user } = useContext(sessionContext)

  const { creative, format, preview, setPreview } = useContext(creativeContext)
  const anchorID = creative.id > 0 ? String(creative.id) : null
  const { comments } = useComments(business.id, 'creative', anchorID)
  // Match the packet badge: surface open (unresolved root) comments, not the raw
  // total. The thread's own mutations invalidate this query, so it stays in sync.
  const unresolvedCount = anchorID ? countUnresolvedRoots(comments, 'creative', anchorID) : 0

  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false)

  // Auto-open the drawer when arriving with a ?commentId= deep link.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('commentId')) setCommentDrawerOpen(true)
  }, [])

  const notificationHeader = `${user?.firstName ?? ''} ${
    user?.lastName ?? ''
  } mentioned you on this creative: "${creative.name}" for business: "${business.businessName}"`

  return (
    <>
      <div className="sm:space-x-0 sm:flex flex-grow sm:p-4 space-x-4">
        <FormPageContent>
          <div className="p-3">
            <p className="my-2 text-xl font-semibold">General Settings</p>
            {getCreativeFormat(format, creative)}
          </div>
        </FormPageContent>

        <FormPagePreview>
          <div className="flex justify-between">
            <div className="flex items-center">
              <Typography variant="h5" className="d-inline-block">
                {adCreativeFormatWire(format)}
              </Typography>
              {creative && (
                <div className="d-inline-block ml-3">
                  <CreativeStatus creative={creative} />
                </div>
              )}
            </div>
            <div className="flex items-center">
              <ButtonGroup variant="contained" aria-label="creative preview format options">
                {creativePreviews.map(x => (
                  <Button
                    key={x.format}
                    color={preview === x.format ? 'secondary' : 'default'}
                    size="large"
                    onClick={() => setPreview(x.format)}
                  >
                    <i className={`${x.icon}`} />
                  </Button>
                ))}
              </ButtonGroup>
            </div>
            <div className="flex items-center">
              <Badge
                badgeContent={unresolvedCount}
                color="secondary"
                overlap="circle"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                className={classes.commentsBadge}
              >
                <Fab
                  variant="extended"
                  color="primary"
                  aria-label="comments"
                  onClick={() => setCommentDrawerOpen(true)}
                >
                  <CommentIcon className="mr-1" />
                  Comments
                </Fab>
              </Badge>
            </div>
          </div>

          <div className="relative h-full">
            <AdPreview />
          </div>
        </FormPagePreview>
      </div>

      <CommentThread
        open={commentDrawerOpen}
        onClose={() => setCommentDrawerOpen(false)}
        businessID={business.id}
        anchor={anchorID ? { type: 'creative', id: anchorID, label: creative.name } : null}
        notificationContext={{
          header: notificationHeader,
          pageUrlBase: window.location.href.split('?')[0],
          slackChannelId: business.slackChannelId,
        }}
      />
    </>
  )
}

export function CreativeWizard({ defaultCreative }: CreativeWizardProps) {
  return (
    <CreativeContextProvider defaultCreative={defaultCreative}>
      <CreativeWizardComponent />
    </CreativeContextProvider>
  )
}
